/**
 * Eternity Public Feed Hierarchy - POC Version
 * Minimal data for Flow A and Flow B demonstration
 */

// ============================================================================
// FEED DEFINITIONS - POC FLOWS ONLY
// ============================================================================

export const FEEDS = {
  HOME: {
    id: 'home',
    name: 'HOME',
    type: 'root',
    parentFeedId: null,
    loopEnabled: true,
    posts: [
      {
        id: 'home-brand',
        type: 'service',
        category: 'FOUNDATION',
        title: 'BRAND SYSTEM',
        subtitle: 'Immovable Foundation & Vision',
        shortCaption: 'Forging brand identity systems with the weight and permanence of granite.',
        fullCaption: 'Forging brand identity systems with the weight and permanence of granite. Every brand system we build is designed for permanence. Visual identity, positioning strategy, and messaging architecture that withstands market shifts.',
        media: {
          type: 'image',
          url: 'https://images.pexels.com/photos/12694101/pexels-photo-12694101.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
          alt: 'Abstract granite foundation'
        },
        cta: {
          type: 'NONE',
          label: 'EXPLORE',
          destination: null
        }
      },
      {
        id: 'home-work',
        type: 'category',
        category: 'PORTFOLIO',
        title: 'OUR WORK',
        subtitle: 'Projects & Portfolio',
        shortCaption: 'Selected work demonstrating strategic creativity and measurable transformation.',
        fullCaption: 'Selected work demonstrating strategic creativity and measurable transformation. Each project represents a deep partnership with brands that trusted us to reshape their market position.',
        media: {
          type: 'image',
          url: 'https://images.unsplash.com/photo-1618005198919-d3d4b5a92ead?crop=entropy&cs=srgb&fm=jpg&q=85',
          alt: 'Abstract creative work'
        },
        cta: {
          type: 'OPEN_FEED',
          label: 'EXPLORE',
          destination: 'work'
        }
      },
      {
        id: 'home-systems',
        type: 'category',
        category: 'SERVICES',
        title: 'SYSTEMS',
        subtitle: 'Strategic Operating Systems',
        shortCaption: 'Integrated systems that transform businesses into permanent market forces.',
        fullCaption: 'Integrated systems that transform businesses into permanent market forces. From brand foundation to digital infrastructure, each system is designed to amplify your authority.',
        media: {
          type: 'image',
          url: 'https://images.unsplash.com/photo-1561212001-d400282c6144?crop=entropy&cs=srgb&fm=jpg&q=85',
          alt: 'Abstract systems'
        },
        cta: {
          type: 'OPEN_FEED',
          label: 'EXPLORE',
          destination: 'systems'
        }
      }
    ]
  },

  // ============================================================================
  // FLOW A: HOME → OUR WORK → ARTWORK
  // ============================================================================
  
  WORK: {
    id: 'work',
    name: 'OUR WORK',
    type: 'category',
    parentFeedId: 'home',
    loopEnabled: true,
    posts: [
      {
        id: 'work-artwork',
        type: 'category',
        category: 'VISUAL ARTS',
        title: 'ARTWORK',
        subtitle: 'Photography & Visual Projects',
        shortCaption: 'Premium visual storytelling across editorial, commercial, and artistic domains.',
        fullCaption: 'Premium visual storytelling across editorial, commercial, and artistic domains. Our photography and visual projects combine technical precision with strategic narrative.',
        media: {
          type: 'image',
          url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?crop=entropy&cs=srgb&fm=jpg&q=85',
          alt: 'Premium photography artwork'
        },
        cta: {
          type: 'OPEN_FEED',
          label: 'VIEW PROJECTS',
          destination: 'artwork'
        }
      },
      {
        id: 'work-campaigns',
        type: 'category',
        category: 'CAMPAIGNS',
        title: 'BRAND CAMPAIGNS',
        subtitle: 'Integrated Campaign Work',
        shortCaption: 'Multi-channel campaigns that reshape market position.',
        fullCaption: 'Multi-channel campaigns that reshape market position and compress competitive timelines. Strategic campaign architecture that integrates brand, content, and growth.',
        media: {
          type: 'image',
          url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=srgb&fm=jpg&q=85',
          alt: 'Brand campaign work'
        },
        cta: {
          type: 'NONE',
          label: 'VIEW CAMPAIGNS',
          destination: null
        }
      }
    ]
  },

  ARTWORK: {
    id: 'artwork',
    name: 'ARTWORK',
    type: 'project_feed',
    parentFeedId: 'work',
    loopEnabled: true,
    posts: [
      {
        id: 'artwork-mountain',
        type: 'project',
        category: 'LANDSCAPE',
        title: 'ETERNAL PEAKS',
        subtitle: 'Alpine Photography Series',
        shortCaption: 'High-altitude photography capturing the permanence and scale of mountain architecture.',
        fullCaption: 'High-altitude photography capturing the permanence and scale of mountain architecture. This series explores the visual language of granite foundations and natural systems engineered for permanence. Shot at 4000+ meters across three continents.',
        media: {
          type: 'image',
          url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?crop=entropy&cs=srgb&fm=jpg&q=85',
          alt: 'Mountain peak photography'
        },
        cta: {
          type: 'NONE',
          label: 'VIEW FULL PROJECT',
          destination: null
        }
      },
      {
        id: 'artwork-urban',
        type: 'project',
        category: 'URBAN',
        title: 'CITY PULSE',
        subtitle: 'Urban Motion Study',
        shortCaption: 'Long-exposure urban cinematography exploring velocity, density, and human systems.',
        fullCaption: 'Long-exposure urban cinematography exploring velocity, density, and human systems. This motion study captures the energy architecture of major cities through light trails and crowd flows.',
        media: {
          type: 'video',
          url: 'https://player.vimeo.com/external/434045526.sd.mp4?s=c27eecc69a27dbc4ff2b87d38afc35f1a9e7c02d',
          poster: 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?crop=entropy&cs=srgb&fm=jpg&q=85',
          alt: 'Urban motion cinematography'
        },
        cta: {
          type: 'NONE',
          label: 'VIEW FULL PROJECT',
          destination: null
        }
      },
      {
        id: 'artwork-portrait',
        type: 'project',
        category: 'PORTRAIT',
        title: 'AUTHORITY SERIES',
        subtitle: 'Executive Portraiture',
        shortCaption: 'Premium executive portraits engineered to communicate competence and vision.',
        fullCaption: 'Premium executive portraits engineered to communicate competence and vision. This portrait series redefines how ambitious leaders present themselves through controlled lighting architecture and strategic visual language.',
        media: {
          type: 'image',
          url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&q=85',
          alt: 'Executive portrait photography'
        },
        cta: {
          type: 'NONE',
          label: 'VIEW FULL SERIES',
          destination: null
        }
      },
      {
        id: 'artwork-product',
        type: 'project',
        category: 'COMMERCIAL',
        title: 'PRECISION OBJECTS',
        subtitle: 'Product & Still Life',
        shortCaption: 'Commercial product photography that transforms objects into icons of precision.',
        fullCaption: 'Commercial product photography that transforms objects into icons of precision and desire. Controlled lighting and architectural composition that elevates product perception from commodity to covetable asset.',
        media: {
          type: 'image',
          url: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?crop=entropy&cs=srgb&fm=jpg&q=85',
          alt: 'Product photography'
        },
        cta: {
          type: 'NONE',
          label: 'VIEW FULL PROJECT',
          destination: null
        }
      },
      {
        id: 'artwork-abstract',
        type: 'project',
        category: 'CONCEPTUAL',
        title: 'VISUAL SYSTEMS',
        subtitle: 'Abstract Visual Language',
        shortCaption: 'Abstract visual systems that communicate complex ideas instantly.',
        fullCaption: 'Abstract visual systems and motion design that communicate complex ideas instantly. This series explores data visualization, kinetic typography, and abstract cinematography as strategic communication tools.',
        media: {
          type: 'image',
          url: 'https://images.unsplash.com/photo-1618005198919-d3d4b5a92ead?crop=entropy&cs=srgb&fm=jpg&q=85',
          alt: 'Abstract visual systems'
        },
        cta: {
          type: 'NONE',
          label: 'VIEW FULL SERIES',
          destination: null
        }
      }
    ]
  },

  // ============================================================================
  // FLOW B: HOME → SYSTEMS → BRAND
  // ============================================================================

  SYSTEMS: {
    id: 'systems',
    name: 'SYSTEMS',
    type: 'category',
    parentFeedId: 'home',
    loopEnabled: true,
    posts: [
      {
        id: 'systems-brand',
        type: 'service',
        category: 'FOUNDATION',
        title: 'BRAND',
        subtitle: 'Identity & Positioning Systems',
        shortCaption: 'Strategic brand architecture designed for permanence and market dominance.',
        fullCaption: 'Strategic brand architecture designed for permanence and market dominance. Our brand systems integrate positioning strategy, visual identity, and messaging architecture into unified foundations.',
        media: {
          type: 'image',
          url: 'https://images.pexels.com/photos/12694101/pexels-photo-12694101.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
          alt: 'Brand foundation metaphor'
        },
        cta: {
          type: 'OPEN_FEED',
          label: 'EXPLORE BRAND',
          destination: 'brand'
        }
      },
      {
        id: 'systems-content',
        type: 'service',
        category: 'PRODUCTION',
        title: 'CONTENT',
        subtitle: 'High-Velocity Production Systems',
        shortCaption: 'Cinematic content engines that produce at velocity without compromising excellence.',
        fullCaption: 'Cinematic content engines that produce at velocity without compromising creative excellence. We build content systems that combine strategic narrative with rapid production velocity.',
        media: {
          type: 'image',
          url: 'https://images.unsplash.com/photo-1687844599821-e0eceea6f6a1?crop=entropy&cs=srgb&fm=jpg&q=85',
          alt: 'Content production system'
        },
        cta: {
          type: 'NONE',
          label: 'EXPLORE CONTENT',
          destination: null
        }
      }
    ]
  },

  BRAND: {
    id: 'brand',
    name: 'BRAND',
    type: 'service_feed',
    parentFeedId: 'systems',
    loopEnabled: true,
    posts: [
      {
        id: 'brand-foundation',
        type: 'service_detail',
        category: 'POSITIONING',
        title: 'FOUNDATION & POSITIONING',
        subtitle: 'Strategic Brand Architecture',
        shortCaption: 'Building immovable brand foundations through strategic positioning.',
        fullCaption: 'Building immovable brand foundations through strategic positioning and market intelligence. We engineer positioning systems that define clear competitive territories and establish permanent market positions.',
        media: {
          type: 'image',
          url: 'https://images.pexels.com/photos/12694101/pexels-photo-12694101.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
          alt: 'Mountain foundation representing brand positioning'
        },
        cta: {
          type: 'NONE',
          label: 'LEARN MORE',
          destination: null
        }
      },
      {
        id: 'brand-identity',
        type: 'service_detail',
        category: 'VISUAL SYSTEMS',
        title: 'IDENTITY & VISUAL LANGUAGE',
        subtitle: 'Designed for Permanence',
        shortCaption: 'Visual identity systems engineered to amplify authority and withstand time.',
        fullCaption: 'Visual identity systems engineered to amplify authority and withstand time. We design comprehensive visual languages that function as strategic assets rather than decorative choices.',
        media: {
          type: 'image',
          url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=srgb&fm=jpg&q=85',
          alt: 'Visual identity system design'
        },
        cta: {
          type: 'NONE',
          label: 'VIEW PROCESS',
          destination: null
        }
      },
      {
        id: 'brand-work',
        type: 'service_detail',
        category: 'CASE STUDY',
        title: 'SELECTED BRAND WORK',
        subtitle: 'Transformation in Action',
        shortCaption: 'Real brand transformations that reshaped markets.',
        fullCaption: 'Real brand transformations that reshaped markets and compressed competitive timelines. This collection showcases strategic brand work across industries with measurable authority amplification.',
        media: {
          type: 'image',
          url: 'https://images.unsplash.com/photo-1634942537034-2531766767d1?crop=entropy&cs=srgb&fm=jpg&q=85',
          alt: 'Brand work showcase'
        },
        cta: {
          type: 'NONE',
          label: 'VIEW CASE STUDIES',
          destination: null
        }
      }
    ]
  }
};

// ============================================================================
// FEED LOOKUP & NAVIGATION HELPERS
// ============================================================================

export function getFeedById(feedId) {
  const feedKey = Object.keys(FEEDS).find(
    key => FEEDS[key].id === feedId
  );
  return feedKey ? FEEDS[feedKey] : null;
}

export function getParentFeed(currentFeedId) {
  const currentFeed = getFeedById(currentFeedId);
  if (!currentFeed || !currentFeed.parentFeedId) return null;
  return getFeedById(currentFeed.parentFeedId);
}

export function isRootFeed(feedId) {
  const feed = getFeedById(feedId);
  return feed && feed.type === 'root';
}

export function getFeedBreadcrumb(feedId) {
  const trail = [];
  let currentFeed = getFeedById(feedId);
  
  while (currentFeed) {
    trail.unshift({ id: currentFeed.id, name: currentFeed.name });
    currentFeed = currentFeed.parentFeedId ? getFeedById(currentFeed.parentFeedId) : null;
  }
  
  return trail;
}
